import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useOnClickOutside } from 'usehooks-ts';

import { FeatureFlagsManager, Flag } from '../../../utils/core/feature-flags-manager';
import { useFeatureFlag } from '../../hooks/use-feature-flag';
import { useTheme } from '../../state/theme-state';
import { Icon } from '../icon/icon';
import { Stack } from '../stack/stack';
import { Switch } from '../switch/switch';

/**
 * Lets an organization admin turn the optional feature flags on and off. A change is saved, so it's
 * kept after a reload, but code that already read the flag only sees it after a reload, which the
 * panel suggests once a flag is changed.
 */
export const FeatureFlagsIcon = () => {
  const { t } = useTranslation();
  const themeContext = useTheme();
  const [open, setOpen] = React.useState(false);
  const [changed, setChanged] = React.useState(false);
  const flags = React.useMemo(() => FeatureFlagsManager.instance.getOptional(), []);
  const title = t('featureFlags.title', { ns: 'navigation' });

  const { refs, strategy, floatingStyles } = useFloating({
    middleware: [offset(), flip(), shift()],
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
  });

  useOnClickOutside([refs.reference, refs.floating] as React.RefObject<HTMLElement>[], () =>
    setOpen(false),
  );

  return (
    <>
      <Component
        aria-expanded={open}
        aria-label={title}
        data-testid="feature-flags"
        highlighted={open}
        ref={refs.setReference}
        textColor={themeContext.getTopMenuTextColor()}
        textHoverColor={themeContext.getTopMenuTextHoverColor()}
        type="button"
        onClick={() => setOpen((x) => !x)}
      >
        <Icon
          fixedWidth={true}
          icon={['fas', 'flag']}
          size={'lg'}
          title={title}
          onHoverStyle={{ icon: ['fad', 'flag'] }}
        />
      </Component>

      {open && (
        <FloatingPortal>
          <Panel
            className="notranslate"
            data-testid="feature-flags-panel"
            ref={refs.setFloating}
            style={{ ...floatingStyles, position: strategy }}
          >
            {/* Sticky, so that the reload suggestion is seen however far the list is scrolled. */}
            <Top>
              <Header>{title}</Header>
              {changed && (
                <ReloadButton type="button" onClick={() => window.location.reload()}>
                  {t('featureFlags.reloadSuggestion', { ns: 'navigation' })}
                </ReloadButton>
              )}
            </Top>
            <Stack direction="column">
              {flags.map((flag) => (
                <FlagRow flag={flag} key={flag} onChange={() => setChanged(true)} />
              ))}
            </Stack>
          </Panel>
        </FloatingPortal>
      )}
    </>
  );
};

const FlagRow = (props: { flag: Flag; onChange: () => void }) => {
  const enabled = useFeatureFlag(props.flag);
  const id = `feature-flag-${props.flag}`;

  return (
    <Row alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
      <label htmlFor={id}>{props.flag}</label>
      <Switch
        checked={enabled}
        id={id}
        size={0.7}
        onChange={() => {
          FeatureFlagsManager.instance.setEnabled(props.flag, !enabled);
          props.onChange();
        }}
      />
    </Row>
  );
};

const Component = styled.button<{
  highlighted: boolean;
  textColor?: string;
  textHoverColor?: string;
}>`
  grid-area: feature-flags;
  color: ${(props) => props.textColor ?? 'inherit'};
  font-size: 1.5rem;
  padding: 0 15px;
  height: 100%;
  background: none;
  border: 0;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.color.gray.darkest};
    color: ${(props) => props.textHoverColor ?? props.textColor ?? 'inherit'};
  }
  background-color: ${(props) => (props.highlighted ? props.theme.color.gray.darkest : 'unset')};
`;

const Panel = styled.div`
  background: white;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);
  overflow-y: auto;
  max-height: 60%;
  z-index: 1000;
  width: 380px;
  box-sizing: border-box;
`;

const Top = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
`;

const Header = styled.div`
  padding: 1em;
  color: ${(props) => props.theme.color.gray.dark};
  border-bottom: 1px solid ${(props) => props.theme.color.gray.lightest};
  user-select: none;
`;

const ReloadButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.75em 1em;
  border: 0;
  background-color: ${(props) => props.theme.color.brand.lightest};
  color: ${(props) => props.theme.color.gray.darkest};
  text-align: left;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const Row = styled(Stack)`
  padding: 0.5em 1em;
  font-size: 0.9em;
  label {
    cursor: pointer;
  }
`;
